// src/components/common/ThemeCard.js
import React from 'react';
import { FaCheck } from 'react-icons/fa';
import './ThemeCard.css';

/**
 * ThemeCard - Visual theme selector card component
 *
 * Displays a clickable card with color swatches representing a theme.
 * Shows a checkmark when selected.
 *
 * @param {Object} props
 * @param {string} props.themeId - Theme identifier (option1, option2, option3)
 * @param {string} props.name - Display name of the theme
 * @param {string} props.description - Short description of the theme
 * @param {Object} props.colors - Color swatches to display
 * @param {string} props.colors.primary - Primary color (main-color)
 * @param {string} props.colors.background - Background color (BackG-color)
 * @param {string} props.colors.accent - Accent color (accent-color)
 * @param {string} props.colors.secondary - Secondary color (accent-color2)
 * @param {boolean} props.isSelected - Whether this theme is currently selected
 * @param {function} props.onClick - Handler for theme selection
 * @param {boolean} props.disabled - Whether the card is disabled
 */
const ThemeCard = ({
  themeId,
  name,
  description,
  colors,
  isSelected = false,
  onClick,
  disabled = false
}) => {
  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(themeId);
    }
  };

  const handleKeyDown = (e) => {
    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(themeId);
    }
  };

  return (
    <div
      className={`theme-card ${isSelected ? 'theme-card--selected' : ''} ${disabled ? 'theme-card--disabled' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={`${name} theme${isSelected ? ' (selected)' : ''}`}
      aria-pressed={isSelected}
    >
      {/* Color Swatches */}
      <div className="theme-card__swatches">
        <div className="theme-card__swatch" style={{ backgroundColor: colors.primary }} aria-label="Primary color" />
        <div className="theme-card__swatch" style={{ backgroundColor: colors.background }} aria-label="Background color" />
        <div className="theme-card__swatch" style={{ backgroundColor: colors.accent }} aria-label="Accent color" />
        <div className="theme-card__swatch" style={{ backgroundColor: colors.secondary }} aria-label="Secondary color" />
      </div>

      {/* Theme Info */}
      <div className="theme-card__info">
        <h4 className="theme-card__name">{name}</h4>
        <p className="theme-card__description">{description}</p>
      </div>

      {/* Selected Indicator */}
      {isSelected && (
        <div className="theme-card__check" aria-hidden="true">
          <FaCheck />
        </div>
      )}

      {/* Selection Ring */}
      <div className="theme-card__ring" aria-hidden="true" />
    </div>
  );
};

export default ThemeCard;
